"use client";

import {
	AlertTriangle,
	Check,
	FileVideo2,
	FolderOpen,
	Info,
	Lock,
	Maximize2,
	Move,
	RefreshCcw,
	ShieldCheck,
	Trash2,
	Unlock,
	UploadCloud,
	X,
	Download,
	Loader2,
} from "lucide-react";
import {
	type ChangeEvent,
	type DragEvent,
	useEffect,
	useRef,
	useState,
} from "react";
import { useLocale, useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/ui";
import { loadFFmpeg, resizeVideo, type ResizeOptions } from "@/lib/ffmpeg";

type ToolPhase =
	| "empty"
	| "inspecting"
	| "ready"
	| "unsupported"
	| "processing"
	| "success"
	| "error";
type PresetKey =
	| "original"
	| "landscape"
	| "portrait"
	| "square"
	| "feed"
	| "custom";
type FitMode = "fit" | "fill";
type BackgroundKey = "dark" | "light" | "blue" | "blur";

interface SizePreset {
	key: Exclude<PresetKey, "original" | "custom">;
	ratio: string;
	width: number;
	height: number;
}

interface FocusPoint {
	key: string;
	label: string;
	x: number;
	y: number;
}

const SIZE_PRESETS: readonly SizePreset[] = [
	{
		key: "landscape",
		ratio: "16:9",
		width: 1920,
		height: 1080,
	},
	{
		key: "portrait",
		ratio: "9:16",
		width: 1080,
		height: 1920,
	},
	{ key: "square", ratio: "1:1", width: 1080, height: 1080 },
	{
		key: "feed",
		ratio: "4:5",
		width: 1080,
		height: 1350,
	},
];

function formatBytes({
	bytes,
	locale,
}: {
	bytes: number;
	locale: string;
}): string {
	if (bytes < 1024) return `${bytes} B`;
	const units = ["KB", "MB", "GB"];
	let value = bytes / 1024;
	let unit = units[0];
	for (let index = 1; value >= 1024 && index < units.length; index += 1) {
		value /= 1024;
		unit = units[index];
	}
	return `${new Intl.NumberFormat(locale, {
		maximumFractionDigits: value >= 10 ? 0 : 1,
	}).format(value)} ${unit}`;
}

function formatDuration(seconds: number): string {
	if (!Number.isFinite(seconds)) return "0:00";
	const rounded = Math.round(seconds);
	const minutes = Math.floor(rounded / 60);
	return `${minutes}:${String(rounded % 60).padStart(2, "0")}`;
}

function greatestCommonDivisor({
	left,
	right,
}: {
	left: number;
	right: number;
}): number {
	let a = Math.round(left);
	let b = Math.round(right);
	while (b !== 0) {
		const remainder = a % b;
		a = b;
		b = remainder;
	}
	return a || 1;
}

function formatRatio({
	width,
	height,
}: {
	width: number;
	height: number;
}): string {
	if (!width || !height) return "—";
	const divisor = greatestCommonDivisor({ left: width, right: height });
	const ratioWidth = width / divisor;
	const ratioHeight = height / divisor;
	if (ratioWidth <= 30 && ratioHeight <= 30)
		return `${ratioWidth}:${ratioHeight}`;
	return (width / height).toFixed(2);
}

export function ResizeVideo() {
	const t = useTranslations("ResizeVideo.tool");
	const locale = useLocale();
	const focusPoints: readonly FocusPoint[] = [
		{ key: "top-left", label: t("focus.topLeft"), x: 0, y: 0 },
		{ key: "top", label: t("focus.topCenter"), x: 50, y: 0 },
		{ key: "top-right", label: t("focus.topRight"), x: 100, y: 0 },
		{ key: "left", label: t("focus.centerLeft"), x: 0, y: 50 },
		{ key: "center", label: t("focus.center"), x: 50, y: 50 },
		{ key: "right", label: t("focus.centerRight"), x: 100, y: 50 },
		{ key: "bottom-left", label: t("focus.bottomLeft"), x: 0, y: 100 },
		{ key: "bottom", label: t("focus.bottomCenter"), x: 50, y: 100 },
		{ key: "bottom-right", label: t("focus.bottomRight"), x: 100, y: 100 },
	];
	const backgrounds: readonly {
		key: BackgroundKey;
		label: string;
		swatch: string;
	}[] = [
		{ key: "dark", label: t("backgrounds.dark"), swatch: "bg-slate-950" },
		{ key: "light", label: t("backgrounds.light"), swatch: "bg-slate-100" },
		{ key: "blue", label: t("backgrounds.blue"), swatch: "bg-blue-600" },
		{
			key: "blur",
			label: t("backgrounds.blurPreview"),
			swatch: "bg-gradient-to-br from-blue-500 to-cyan-300",
		},
	];
	const inputRef = useRef<HTMLInputElement>(null);
	const videoRef = useRef<HTMLVideoElement>(null);
	const backgroundVideoRef = useRef<HTMLVideoElement>(null);
	const videoUrlRef = useRef<string | null>(null);
	const [file, setFile] = useState<File | null>(null);
	const [videoUrl, setVideoUrl] = useState<string | null>(null);
	const [phase, setPhase] = useState<ToolPhase>("empty");
	const [error, setError] = useState<string | null>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [duration, setDuration] = useState(0);
	const [sourceSize, setSourceSize] = useState({ width: 0, height: 0 });
	const [preset, setPreset] = useState<PresetKey>("original");
	const [targetWidth, setTargetWidth] = useState(1920);
	const [targetHeight, setTargetHeight] = useState(1080);
	const [ratioLocked, setRatioLocked] = useState(true);
	const [fitMode, setFitMode] = useState<FitMode>("fit");
	const [background, setBackground] = useState<BackgroundKey>("dark");
	const [focusPoint, setFocusPoint] = useState("center");
	const [format, setFormat] = useState("MP4");
	const [quality, setQuality] = useState("Balanced");
	const [keepAudio, setKeepAudio] = useState(true);
	const [noticeVisible, setNoticeVisible] = useState(false);
	const [progress, setProgress] = useState(0);
	const [resultBlob, setResultBlob] = useState<Blob | null>(null);
	const [processingError, setProcessingError] = useState<string | null>(null);
	const outputUrlRef = useRef<string | null>(null);

	useEffect(
		() => () => {
			if (videoUrlRef.current) URL.revokeObjectURL(videoUrlRef.current);
			if (outputUrlRef.current) URL.revokeObjectURL(outputUrlRef.current);
		},
		[],
	);

	const markChanged = () => setNoticeVisible(false);

	const resetFile = () => {
		if (videoUrlRef.current) URL.revokeObjectURL(videoUrlRef.current);
		videoUrlRef.current = null;
		setVideoUrl(null);
		setFile(null);
		setPhase("empty");
		setError(null);
		setDuration(0);
		setSourceSize({ width: 0, height: 0 });
		setPreset("original");
		setTargetWidth(1920);
		setTargetHeight(1080);
		setFitMode("fit");
		setFocusPoint("center");
		setNoticeVisible(false);
		if (inputRef.current) inputRef.current.value = "";
	};

	const chooseFile = (nextFile?: File) => {
		if (!nextFile) return;
		setNoticeVisible(false);
		setError(null);
		if (!nextFile.type.startsWith("video/")) {
			if (videoUrlRef.current) URL.revokeObjectURL(videoUrlRef.current);
			videoUrlRef.current = null;
			setVideoUrl(null);
			setFile(null);
			setPhase("empty");
			setError(t("errors.invalidFile"));
			return;
		}
		setPhase("inspecting");
		if (videoUrlRef.current) URL.revokeObjectURL(videoUrlRef.current);
		const nextUrl = URL.createObjectURL(nextFile);
		videoUrlRef.current = nextUrl;
		setVideoUrl(nextUrl);
		setFile(nextFile);
	};

	const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
		chooseFile(event.target.files?.[0]);
	};

	const handleDrop = (event: DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setIsDragging(false);
		chooseFile(event.dataTransfer.files[0]);
	};

	const handleMetadata = () => {
		const video = videoRef.current;
		if (
			!video ||
			!Number.isFinite(video.duration) ||
			video.duration <= 0 ||
			!video.videoWidth ||
			!video.videoHeight
		) {
			setPhase("unsupported");
			setError(t("errors.metadata"));
			return;
		}
		setDuration(video.duration);
		setSourceSize({ width: video.videoWidth, height: video.videoHeight });
		setTargetWidth(video.videoWidth);
		setTargetHeight(video.videoHeight);
		setPreset("original");
		setPhase("ready");
	};

	const handleVideoError = () => {
		setPhase("unsupported");
		setError(t("errors.preview"));
	};

	const handleResize = async () => {
		if (!file || phase !== "ready") return;

		setProcessingError(null);
		setProgress(0);
		setPhase("processing");

		try {
			await loadFFmpeg();

			// Find active focus point
			const activeFocus =
				focusPoints.find((point) => point.key === focusPoint) ?? focusPoints[4];

			const options: ResizeOptions = {
				targetWidth,
				targetHeight,
				fitMode,
				focusX: activeFocus.x,
				focusY: activeFocus.y,
				background,
				format,
				quality,
				keepAudio,
			};

			const blob = await resizeVideo({
				file,
				options,
				onProgress: (p) => setProgress(p.percent),
			});

			setResultBlob(blob);
			setPhase("success");

			// Create download URL
			if (outputUrlRef.current) URL.revokeObjectURL(outputUrlRef.current);
			const url = URL.createObjectURL(blob);
			outputUrlRef.current = url;
		} catch {
			setProcessingError(t("errors.processing"));
			setPhase("error");
		}
	};

	const handleDownload = () => {
		if (!resultBlob || !file) return;

		const url = outputUrlRef.current;
		if (!url) return;

		const a = document.createElement("a");
		a.href = url;
		// Generate output filename: original-name-resized.ext
		const originalBase = file.name.replace(/\.[^/.]+$/, "");
		const ext = format.toLowerCase();
		a.download = `${originalBase}-resized.${ext}`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
	};

	const resetProcessing = () => {
		setProgress(0);
		setResultBlob(null);
		setProcessingError(null);
		if (outputUrlRef.current) {
			URL.revokeObjectURL(outputUrlRef.current);
			outputUrlRef.current = null;
		}
		setPhase("ready");
	};

	const selectPreset = (nextPreset: PresetKey) => {
		markChanged();
		setPreset(nextPreset);
		setRatioLocked(true);
		if (nextPreset === "original") {
			setTargetWidth(sourceSize.width);
			setTargetHeight(sourceSize.height);
			return;
		}
		const size = SIZE_PRESETS.find((candidate) => candidate.key === nextPreset);
		if (size) {
			setTargetWidth(size.width);
			setTargetHeight(size.height);
		}
	};

	const updateWidth = (value: number) => {
		markChanged();
		setPreset("custom");
		if (ratioLocked && targetWidth > 0 && targetHeight > 0 && value > 0) {
			setTargetHeight(
				Math.max(1, Math.round(value / (targetWidth / targetHeight))),
			);
		}
		setTargetWidth(value);
	};

	const updateHeight = (value: number) => {
		markChanged();
		setPreset("custom");
		if (ratioLocked && targetWidth > 0 && targetHeight > 0 && value > 0) {
			setTargetWidth(
				Math.max(1, Math.round(value * (targetWidth / targetHeight))),
			);
		}
		setTargetHeight(value);
	};

	const activeFocus =
		focusPoints.find((point) => point.key === focusPoint) ?? focusPoints[4];
	const dimensionError =
		targetWidth < 16 ||
		targetWidth > 7680 ||
		targetHeight < 16 ||
		targetHeight > 7680;
	const targetRatio =
		!dimensionError && targetHeight > 0 ? targetWidth / targetHeight : 16 / 9;
	const isUpscaling =
		phase === "ready" &&
		(targetWidth > sourceSize.width || targetHeight > sourceSize.height);
	const sourceRatio = sourceSize.height
		? sourceSize.width / sourceSize.height
		: 16 / 9;
	const cropsEdges =
		phase === "ready" &&
		fitMode === "fill" &&
		Math.abs(sourceRatio - targetRatio) > 0.01;
	const previewWidth =
		targetRatio < 0.72 ? "58%" : targetRatio < 1 ? "78%" : "100%";
	const backgroundClass = {
		dark: "bg-slate-950",
		light: "bg-slate-100",
		blue: "bg-blue-600",
		blur: "bg-slate-900",
	}[background];

	const syncBackground = () => {
		const foreground = videoRef.current;
		const backdrop = backgroundVideoRef.current;
		if (!foreground || !backdrop) return;
		if (Math.abs(backdrop.currentTime - foreground.currentTime) > 0.2) {
			backdrop.currentTime = foreground.currentTime;
		}
	};

	return (
		<section
			aria-label={t("ariaLabel")}
			className="mx-auto w-full max-w-[84rem]"
		>
			<div className="overflow-hidden rounded-[2rem] border border-blue-200/80 bg-slate-50/90 shadow-[0_28px_80px_-42px_rgba(37,99,235,0.48)] backdrop-blur-xl dark:border-blue-900/70 dark:bg-slate-950/88 dark:shadow-[0_28px_80px_-42px_rgba(34,211,238,0.25)]">
				<div className="flex flex-col gap-3 border-b border-blue-100 bg-white/80 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-7 dark:border-blue-950 dark:bg-slate-950/80">
					<div>
						<h2 className="font-bold tracking-tight">{t("header.title")}</h2>
						<p className="text-muted-foreground mt-0.5 text-sm">
							{t("header.description")}
						</p>
					</div>
					<span className="inline-flex min-h-8 w-fit items-center gap-1.5 rounded-full border border-cyan-200 bg-cyan-50 px-3 text-xs font-semibold text-cyan-950 dark:border-cyan-900 dark:bg-cyan-950/50 dark:text-cyan-100">
						<ShieldCheck className="size-3.5" aria-hidden="true" />{" "}
						{t("header.local")}
					</span>
				</div>

				<div className="p-4 sm:p-6 lg:p-8">
					{!file ? (
						<div
							className={cn(
								"relative flex min-h-80 flex-col items-center justify-center rounded-3xl border-2 border-dashed bg-white/90 px-6 text-center shadow-inner transition-colors dark:bg-slate-900/80",
								isDragging
									? "border-cyan-400 bg-cyan-50/80 dark:border-cyan-400 dark:bg-cyan-950/30"
									: "border-blue-200 hover:border-blue-400 dark:border-blue-900 dark:hover:border-cyan-600",
							)}
							onDragEnter={(event) => {
								event.preventDefault();
								setIsDragging(true);
							}}
							onDragOver={(event) => event.preventDefault()}
							onDragLeave={(event) => {
								if (
									!(event.relatedTarget instanceof Node) ||
									!event.currentTarget.contains(event.relatedTarget)
								) {
									setIsDragging(false);
								}
							}}
							onDrop={handleDrop}
						>
							<div className="flex size-16 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-400 text-white shadow-lg shadow-blue-500/20">
								<UploadCloud className="size-7" aria-hidden="true" />
							</div>
							<p className="mt-6 text-2xl font-bold tracking-tight">
								{t("drop.title")}
							</p>
							<Button
								className="mt-6 h-12 rounded-xl bg-blue-600 px-6 text-white shadow-lg shadow-blue-600/20 hover:bg-blue-500"
								onClick={() => inputRef.current?.click()}
							>
								<FolderOpen className="size-4.5" aria-hidden="true" />{" "}
								{t("drop.choose")}
							</Button>
							<input
								ref={inputRef}
								type="file"
								accept="video/*"
								className="sr-only"
								onChange={handleInputChange}
							/>
							<p className="mt-5 inline-flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
								<ShieldCheck
									className="size-3.5 text-cyan-600"
									aria-hidden="true"
								/>
								{t("drop.privacy")}
							</p>
						</div>
					) : (
						<div className="grid gap-6 lg:grid-cols-[minmax(0,1.08fr)_minmax(22rem,0.92fr)]">
							<div className="min-w-0 space-y-4">
								<div className="rounded-2xl border border-blue-100 bg-white p-4 shadow-sm sm:p-5 dark:border-blue-950 dark:bg-slate-900">
									<div className="mb-4 flex items-center justify-between gap-3">
										<div className="min-w-0">
											<p className="truncate font-semibold">{file.name}</p>
											<p className="text-muted-foreground mt-0.5 text-xs">
												{formatBytes({ bytes: file.size, locale })}
												{sourceSize.width
													? ` · ${sourceSize.width}×${sourceSize.height}`
													: ""}
												{duration ? ` · ${formatDuration(duration)}` : ""}
											</p>
										</div>
										<Button
											variant="ghost"
											size="icon"
											className="size-10 shrink-0 rounded-xl"
											onClick={resetFile}
											aria-label={t("remove")}
										>
											<Trash2 className="size-4" aria-hidden="true" />
										</Button>
									</div>

									<div className="flex min-h-96 items-center justify-center overflow-hidden rounded-2xl bg-slate-200/70 p-3 dark:bg-slate-950">
										{phase === "inspecting" ? (
											<div
												className="flex flex-col items-center gap-3 text-slate-600 dark:text-slate-300"
												role="status"
											>
												<RefreshCcw
													className="size-6 animate-spin motion-reduce:animate-none"
													aria-hidden="true"
												/>
												{t("reading")}
											</div>
										) : null}
										{videoUrl ? (
											<div
												className={cn(
													"relative mx-auto max-h-[31rem] overflow-hidden rounded-xl shadow-xl transition-[width,aspect-ratio] duration-200 motion-reduce:transition-none",
													backgroundClass,
													phase === "inspecting" && "invisible",
												)}
												style={{
													aspectRatio: `${targetRatio}`,
													width: previewWidth,
												}}
											>
												{fitMode === "fit" && background === "blur" ? (
													<video
														ref={backgroundVideoRef}
														src={videoUrl}
														muted
														aria-hidden="true"
														tabIndex={-1}
														className="absolute inset-[-8%] h-[116%] w-[116%] scale-110 object-cover opacity-70 blur-xl"
													/>
												) : null}
												{/* User-selected videos do not provide a captions track to this local preview. */}
												{/* eslint-disable-next-line jsx-a11y/media-has-caption */}
												<video
													ref={videoRef}
													src={videoUrl}
													controls
													preload="metadata"
													className={cn(
														"relative z-10 h-full w-full",
														fitMode === "fit"
															? "object-contain"
															: "object-cover",
													)}
													style={{
														objectPosition: `${activeFocus.x}% ${activeFocus.y}%`,
													}}
													onLoadedMetadata={handleMetadata}
													onError={handleVideoError}
													onPlay={() => void backgroundVideoRef.current?.play()}
													onPause={() => backgroundVideoRef.current?.pause()}
													onSeeked={syncBackground}
													onTimeUpdate={syncBackground}
												/>
											</div>
										) : null}
									</div>

									{phase === "ready" ? (
										<div className="mt-4 grid grid-cols-2 gap-3 rounded-xl bg-slate-50 p-3 text-center text-xs sm:grid-cols-4 dark:bg-slate-950">
											<div>
												<span className="text-muted-foreground block">
													{t("metrics.original")}
												</span>
												<strong className="mt-1 block">
													{sourceSize.width}×{sourceSize.height}
												</strong>
											</div>
											<div>
												<span className="text-muted-foreground block">
													{t("metrics.target")}
												</span>
												<strong className="mt-1 block">
													{targetWidth}×{targetHeight}
												</strong>
											</div>
											<div>
												<span className="text-muted-foreground block">
													{t("metrics.ratio")}
												</span>
												<strong className="mt-1 block">
													{formatRatio({
														width: targetWidth,
														height: targetHeight,
													})}
												</strong>
											</div>
											<div>
												<span className="text-muted-foreground block">
													{t("metrics.mode")}
												</span>
												<strong className="mt-1 block capitalize">
													{fitMode}
												</strong>
											</div>
										</div>
									) : null}
								</div>

								{cropsEdges ? (
									<div
										className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950/35 dark:text-amber-100"
										role="status"
									>
										<div className="flex items-start gap-3">
											<Info
												className="mt-0.5 size-4.5 shrink-0"
												aria-hidden="true"
											/>
											<div>
												<p className="font-bold">{t("crop.title")}</p>
												<p className="mt-1 leading-6">
													{t("crop.description")}
												</p>
											</div>
										</div>
									</div>
								) : null}
							</div>

							<div className="min-w-0 space-y-4">
								<div className="rounded-2xl border border-blue-100 bg-white p-5 shadow-sm dark:border-blue-950 dark:bg-slate-900">
									<div className="flex items-center justify-between gap-3">
										<p className="font-bold">{t("canvas.title")}</p>
										<Maximize2
											className="size-4.5 text-cyan-600 dark:text-cyan-300"
											aria-hidden="true"
										/>
									</div>
									<div
										className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3"
										role="group"
										aria-label={t("canvas.presetLabel")}
									>
										<button
											type="button"
											aria-pressed={preset === "original"}
											disabled={phase !== "ready"}
											onClick={() => selectPreset("original")}
											className={cn(
												"min-h-14 rounded-xl border px-3 py-2 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50",
												preset === "original"
													? "border-blue-500 bg-blue-50 text-blue-900 dark:border-cyan-500 dark:bg-cyan-950/40 dark:text-cyan-100"
													: "border-slate-200 bg-slate-50 hover:bg-slate-100 dark:border-slate-800 dark:bg-slate-950 dark:hover:bg-slate-800",
											)}
										>
											<span className="font-semibold">
												{t("canvas.original")}
											</span>
											<span className="text-muted-foreground mt-0.5 block text-xs">
												{sourceSize.width
													? `${formatRatio({
															width: sourceSize.width,
															height: sourceSize.height,
														})}`
													: t("canvas.sourceRatio")}
											</span>
										</button>
										{SIZE_PRESETS.map((size) => (
											<button
												key={size.key}
												type="button"
												aria-pressed={preset === size.key}
												disabled={phase !== "ready"}
												onClick={() => selectPreset(size.key)}
												className={cn(
													"min-h-14 rounded-xl border px-3 py-2 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50",
													preset === size.key
														? "border-blue-500 bg-blue-50 text-blue-900 dark:border-cyan-500 dark:bg-cyan-950/40 dark:text-cyan-100"
														: "border-slate-200 bg-slate-50 hover:bg-slate-100 dark:border-slate-800 dark:bg-slate-950 dark:hover:bg-slate-800",
												)}
											>
												<span className="font-semibold">{size.ratio}</span>
												<span className="text-muted-foreground mt-0.5 block text-xs">
													{size.width}×{size.height}
												</span>
											</button>
										))}
									</div>

									<div className="mt-4 grid grid-cols-[1fr_auto_1fr] items-end gap-2">
										<label className="text-sm font-semibold">
											{t("canvas.width")}
											<input
												type="number"
												min={16}
												max={7680}
												value={targetWidth}
												disabled={phase !== "ready"}
												aria-invalid={dimensionError}
												onChange={(event) =>
													updateWidth(Number(event.target.value))
												}
												className="bg-background mt-2 h-11 w-full rounded-xl border px-3 outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
											/>
										</label>
										<button
											type="button"
											aria-label={
												ratioLocked
													? t("canvas.unlockRatio")
													: t("canvas.lockRatio")
											}
											aria-pressed={ratioLocked}
											disabled={phase !== "ready"}
											onClick={() => {
												setRatioLocked((locked) => !locked);
												markChanged();
											}}
											className="mb-0.5 flex size-10 items-center justify-center rounded-xl border bg-slate-50 transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 dark:bg-slate-950 dark:hover:bg-slate-800"
										>
											{ratioLocked ? (
												<Lock className="size-4" aria-hidden="true" />
											) : (
												<Unlock className="size-4" aria-hidden="true" />
											)}
										</button>
										<label className="text-sm font-semibold">
											{t("canvas.height")}
											<input
												type="number"
												min={16}
												max={7680}
												value={targetHeight}
												disabled={phase !== "ready"}
												aria-invalid={dimensionError}
												onChange={(event) =>
													updateHeight(Number(event.target.value))
												}
												className="bg-background mt-2 h-11 w-full rounded-xl border px-3 outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
											/>
										</label>
									</div>
									{dimensionError ? (
										<p
											className="mt-3 text-sm text-red-700 dark:text-red-300"
											role="alert"
										>
											{t("canvas.dimensionError")}
										</p>
									) : null}
								</div>

								<div className="rounded-2xl border border-blue-100 bg-white p-5 shadow-sm dark:border-blue-950 dark:bg-slate-900">
									<div className="flex items-center justify-between gap-3">
										<p className="font-bold">{t("fit.title")}</p>
										<Move
											className="size-4.5 text-cyan-600 dark:text-cyan-300"
											aria-hidden="true"
										/>
									</div>
									<div
										className="mt-4 grid grid-cols-2 gap-2"
										role="group"
										aria-label={t("fit.modeLabel")}
									>
										{(["fit", "fill"] as const).map((mode) => (
											<button
												key={mode}
												type="button"
												aria-pressed={fitMode === mode}
												disabled={phase !== "ready"}
												onClick={() => {
													setFitMode(mode);
													markChanged();
												}}
												className={cn(
													"min-h-12 rounded-xl border px-3 text-sm font-semibold capitalize transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50",
													fitMode === mode
														? "border-blue-500 bg-blue-50 text-blue-900 dark:border-cyan-500 dark:bg-cyan-950/40 dark:text-cyan-100"
														: "border-slate-200 bg-slate-50 hover:bg-slate-100 dark:border-slate-800 dark:bg-slate-950 dark:hover:bg-slate-800",
												)}
											>
												{t(`fit.${mode}`)}
												{fitMode === mode ? (
													<Check
														className="ml-2 inline size-3.5"
														aria-hidden="true"
													/>
												) : null}
											</button>
										))}
									</div>

									{fitMode === "fill" ? (
										<div className="mt-4">
											<p className="text-sm font-semibold">
												{t("fit.subjectFocus")}
											</p>
											<div
												className="mt-2 grid w-32 grid-cols-3 gap-1.5"
												role="group"
												aria-label={t("fit.focusLabel")}
											>
												{focusPoints.map((point) => (
													<button
														key={point.key}
														type="button"
														aria-label={point.label}
														aria-pressed={focusPoint === point.key}
														onClick={() => {
															setFocusPoint(point.key);
															markChanged();
														}}
														className={cn(
															"size-9 rounded-lg border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
															focusPoint === point.key
																? "border-blue-500 bg-blue-600 text-white"
																: "border-slate-200 bg-slate-50 hover:bg-slate-100 dark:border-slate-800 dark:bg-slate-950 dark:hover:bg-slate-800",
														)}
													>
														<span className="sr-only">{point.label}</span>
														<span
															className={cn(
																"mx-auto block size-1.5 rounded-full",
																focusPoint === point.key
																	? "bg-white"
																	: "bg-slate-400",
															)}
															aria-hidden="true"
														/>
													</button>
												))}
											</div>
										</div>
									) : (
										<div className="mt-4">
											<p className="text-sm font-semibold">
												{t("fit.background")}
											</p>
											<div
												className="mt-2 grid grid-cols-4 gap-2"
												role="group"
												aria-label={t("fit.backgroundLabel")}
											>
												{backgrounds.map((item) => (
													<button
														key={item.key}
														type="button"
														aria-label={t("backgrounds.optionLabel", {
															background: item.label,
														})}
														aria-pressed={background === item.key}
														onClick={() => {
															setBackground(item.key);
															markChanged();
														}}
														className={cn(
															"flex min-h-11 items-center justify-center rounded-xl border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
															background === item.key
																? "border-blue-500 ring-1 ring-blue-500"
																: "border-slate-200 dark:border-slate-800",
														)}
													>
														<span
															className={cn(
																"size-5 rounded-full border border-black/10",
																item.swatch,
															)}
															aria-hidden="true"
														/>
														<span className="sr-only">{item.label}</span>
													</button>
												))}
											</div>
										</div>
									)}
								</div>

								<details className="group rounded-2xl border border-blue-100 bg-white px-5 py-3 shadow-sm dark:border-blue-950 dark:bg-slate-900">
									<summary className="flex min-h-11 cursor-pointer list-none items-center justify-between rounded-lg font-semibold outline-none focus-visible:ring-2 focus-visible:ring-ring">
										{t("output.title")}
										<span className="text-muted-foreground text-xl transition-transform group-open:rotate-45 motion-reduce:transition-none">
											+
										</span>
									</summary>
									<div className="grid gap-4 border-t pt-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
										<label className="text-sm font-semibold">
											{t("output.format")}
											<select
												value={format}
												disabled={phase !== "ready"}
												onChange={(event) => {
													setFormat(event.target.value);
													markChanged();
												}}
												className="bg-background mt-2 h-11 w-full rounded-xl border px-3 outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
											>
												<option>MP4</option>
												<option>WebM</option>
											</select>
										</label>
										<label className="text-sm font-semibold">
											{t("output.quality")}
											<select
												value={quality}
												disabled={phase !== "ready"}
												onChange={(event) => {
													setQuality(event.target.value);
													markChanged();
												}}
												className="bg-background mt-2 h-11 w-full rounded-xl border px-3 outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
											>
												<option value="Smaller">{t("output.smaller")}</option>
												<option value="Balanced">{t("output.balanced")}</option>
												<option value="Crisper">{t("output.crisper")}</option>
											</select>
										</label>
									</div>
									<label className="mt-4 flex min-h-11 cursor-pointer items-center justify-between rounded-xl border bg-slate-50 px-3 text-sm font-semibold dark:bg-slate-950">
										{t("output.keepAudio")}
										<input
											type="checkbox"
											checked={keepAudio}
											disabled={phase !== "ready"}
											onChange={(event) => {
												setKeepAudio(event.target.checked);
												markChanged();
											}}
											className="size-4 accent-blue-600"
										/>
									</label>
								</details>

								{isUpscaling ? (
									<div
										className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950/35 dark:text-amber-100"
										role="status"
									>
										<div className="flex items-start gap-3">
											<AlertTriangle
												className="mt-0.5 size-4.5 shrink-0"
												aria-hidden="true"
											/>
											<div>
												<p className="font-bold">{t("upscale.title")}</p>
												<p className="mt-1 leading-6">
													{t("upscale.description")}
												</p>
												<button
													type="button"
													onClick={() => selectPreset("original")}
													className="mt-2 min-h-9 rounded-lg font-semibold underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
												>
													{t("upscale.useOriginal")}
												</button>
											</div>
										</div>
									</div>
								) : null}

								{error ? (
									<div
										className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-100"
										role="alert"
									>
										<div className="flex items-start justify-between gap-3">
											<p className="leading-6">{error}</p>
											<button
												type="button"
												onClick={() => setError(null)}
												className="rounded-lg p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
												aria-label={t("errors.dismiss")}
											>
												<X className="size-4" aria-hidden="true" />
											</button>
										</div>
										<Button
											variant="outline"
											className="mt-3 h-10 rounded-xl"
											onClick={() => inputRef.current?.click()}
										>
											{t("actions.chooseAnother")}
										</Button>
									</div>
								) : null}

								{phase === "processing" ? (
									<div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-center dark:border-blue-900 dark:bg-blue-950/40">
										<div className="mb-3 flex justify-center">
											<Loader2
												className="size-8 animate-spin text-blue-600 dark:text-cyan-300"
												aria-hidden="true"
											/>
										</div>
										<p className="font-semibold text-blue-900 dark:text-blue-100">
											{t("actions.processing", { progress })}
										</p>
										<div className="mt-3 h-2 overflow-hidden rounded-full bg-blue-200 dark:bg-blue-900">
											<div
												className="h-full bg-blue-600 transition-all duration-300 dark:bg-cyan-400"
												style={{ width: `${progress}%` }}
											/>
										</div>
									</div>
								) : phase === "success" ? (
									<div className="rounded-2xl border border-green-200 bg-green-50 p-4 dark:border-green-900 dark:bg-green-950/40">
										<div className="flex items-start gap-3">
											<Check
												className="mt-0.5 size-5 shrink-0 text-green-600 dark:text-green-300"
												aria-hidden="true"
											/>
											<div className="flex-1">
												<p className="font-semibold text-green-900 dark:text-green-100">
													{t("actions.success")}
												</p>
												<p className="mt-1 text-sm text-green-800 dark:text-green-200">
													{t("actions.outputSize", {
														size: formatBytes({
															bytes: resultBlob?.size || 0,
															locale,
														}),
													})}
												</p>
											</div>
										</div>
										<div className="mt-4 flex gap-2">
											<Button
												className="flex-1 rounded-xl bg-green-600 text-white hover:bg-green-500"
												onClick={handleDownload}
											>
												<Download className="mr-2 size-4" aria-hidden="true" />
												{t("actions.download")}
											</Button>
											<Button
												variant="outline"
												className="rounded-xl"
												onClick={resetProcessing}
											>
												{t("actions.adjust")}
											</Button>
										</div>
									</div>
								) : phase === "error" ? (
									<div className="rounded-2xl border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950/40">
										<div className="flex items-start gap-3">
											<AlertTriangle
												className="mt-0.5 size-5 shrink-0 text-red-600 dark:text-red-300"
												aria-hidden="true"
											/>
											<div className="flex-1">
												<p className="font-semibold text-red-900 dark:text-red-100">
													{t("actions.failed")}
												</p>
												<p className="mt-1 text-sm text-red-800 dark:text-red-200">
													{processingError || t("errors.processing")}
												</p>
											</div>
										</div>
										<Button
											variant="outline"
											className="mt-4 w-full rounded-xl"
											onClick={resetProcessing}
										>
											{t("actions.tryAgain")}
										</Button>
									</div>
								) : (
									<>
										<Button
											className="h-13 w-full rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 text-base text-white shadow-lg shadow-blue-600/20 hover:from-blue-500 hover:to-cyan-400"
											disabled={phase !== "ready" || dimensionError}
											onClick={handleResize}
											title={
												dimensionError ? t("actions.dimensionTitle") : undefined
											}
										>
											<FileVideo2 className="size-4.5" aria-hidden="true" />{" "}
											{t("actions.resize")}
										</Button>
										{noticeVisible ? (
											<div
												className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-950 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-100"
												role="status"
												aria-live="polite"
											>
												<p className="font-bold">{t("notice.title")}</p>
												<p className="mt-1 leading-6">
													{t("notice.description")}
												</p>
											</div>
										) : null}
									</>
								)}
								<input
									ref={inputRef}
									type="file"
									accept="video/*"
									className="sr-only"
									onChange={handleInputChange}
								/>
							</div>
						</div>
					)}

					{!file && error ? (
						<p
							className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-100"
							role="alert"
						>
							{error}
						</p>
					) : null}
				</div>
			</div>
		</section>
	);
}
